import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import QueryEditor from './QueryEditor';
import QueryResults from './QueryResults';
import ExplorerForQueryBuilder from '../metadata/ExplorerForQueryBuilder';
import QueryGeneratorRealtime from './QueryGeneratorRealtime';
import QueryExamples from './QueryExamples';

const QueryBuilderContent = ({
  initialQuery,
  queryMode,
  resultFormat,
  sidebarOpen,
  showTitle = true,
  vertical = false,
  resultsId = 'query-builder-results',
}) => {
  const [queryResults, setQueryResults] = useState(null);
  const [isGeneratorAvailable, setIsGeneratorAvailable] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    const checkGeneratorAvailability = async () => {
      try {
        const response = await fetch('/adss/v1/llm/status');
        if (response.ok) {
          const data = await response.json();
          setIsGeneratorAvailable(data.available);
        }
      } catch (error) {
        console.error('Error checking LLM availability:', error);
        setIsGeneratorAvailable(false);
      }
    };

    checkGeneratorAvailability();
  }, []);

  const handleResultsChange = (newResults) => {
    setQueryResults(newResults);
    if (newResults) {
      setTimeout(() => {
        const resultsElement = document.getElementById(resultsId);
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handleDatabaseItemClick = (text) => {
    if (editorRef.current) {
      editorRef.current.insertTextAtCursor(text);
    }
  };

  const handleGeneratedQuery = (query) => {
    if (editorRef.current) {
      editorRef.current.setContent(query);
    }
  };

  const handleExampleClick = (query) => {
    if (editorRef.current) {
      editorRef.current.setContent(query);
    }
  };

const titleBlock = showTitle && (
  <div className="mb-6">
    <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
      Query Builder
    </h1>

    <p className="text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary mt-1">
      Build and run queries over astronomical catalogs.
    </p>

    <div className="mt-4 rounded-lg border border-ui-light-border dark:border-ui-dark-border bg-ui-light-surface-secondary dark:bg-ui-dark-surface-secondary px-4 py-3">
      <p className="text-sm text-ui-light-text-primary dark:text-ui-dark-text-primary">
        <span className="font-semibold">New:</span>{' '}
        now supports{' '}
        <Link
          to="/documentation/astroql"
          className="font-semibold text-primary-600 dark:text-primary-400 hover:underline"
        >
          AstroQL
        </Link>
        , our new query language designed to succeed ADQL in this system, taking advantage
        of our database organization and spatial indexing.
      </p>

      <Link
        to="/documentation/astroql"
        className="inline-flex items-center mt-2 text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline"
      >
        Read the AstroQL documentation →
      </Link>
    </div>
  </div>
);

  const editorBlock = (
    <QueryEditor
      ref={editorRef}
      onResultsChange={handleResultsChange}
      initialQuery={initialQuery}
      queryMode={queryMode}
      resultFormat={resultFormat}
    />
  );

  const resultsBlock = (
    <div id={resultsId}>
      <QueryResults results={queryResults} />
    </div>
  );

  if (vertical) {
    return (
      <div className="flex flex-col gap-6">
        {titleBlock}
        {sidebarOpen && <ExplorerForQueryBuilder onItemClick={handleDatabaseItemClick} />}
        {editorBlock}
        {resultsBlock}
        {sidebarOpen && (
          <>
            {/* <QueryGeneratorRealtime
              onQueryGenerated={handleGeneratedQuery}
              isGeneratorAvailable={isGeneratorAvailable}
            />
            <QueryExamples onExampleClick={handleExampleClick} /> */}
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      {titleBlock}
      <div className="flex flex-col md:flex-row gap-6 h-full">
        {sidebarOpen && (
          <div className="md:w-80 lg:w-96 flex-shrink-0">
            <div className="mb-4">
              <ExplorerForQueryBuilder onItemClick={handleDatabaseItemClick} />
            </div>
            <div className="mb-4">
              <QueryGeneratorRealtime
                onQueryGenerated={handleGeneratedQuery}
                isGeneratorAvailable={isGeneratorAvailable}
              />
            </div>
            <div>
              <QueryExamples onExampleClick={handleExampleClick} />
            </div>
          </div>
        )}
        <div className="flex-1 flex flex-col gap-6 min-w-0">
          {editorBlock}
          {resultsBlock}
        </div>
      </div>
    </div>
  );
};

export default QueryBuilderContent;
